/*
Copyright (c) 2015, salesforce.com, inc. All rights reserved.
Copyright (c) 2017, Picsa Advisory Services (Pty) Ltd. All rights reserved.

Redistribution and use in source and binary forms, with or without modification, are permitted provided that the following conditions are met:
Redistributions of source code must retain the above copyright notice, this list of conditions and the following disclaimer.
Redistributions in binary form must reproduce the above copyright notice, this list of conditions and the following disclaimer in the documentation and/or other materials provided with the distribution.
Neither the name of salesforce.com, inc. nor the names of its contributors may be used to endorse or promote products derived from this software without specific prior written permission.

THIS SOFTWARE IS PROVIDED BY THE COPYRIGHT HOLDERS AND CONTRIBUTORS "AS IS" AND ANY EXPRESS OR IMPLIED WARRANTIES, INCLUDING, BUT NOT LIMITED TO, THE IMPLIED WARRANTIES OF MERCHANTABILITY AND FITNESS FOR A PARTICULAR PURPOSE ARE DISCLAIMED. IN NO EVENT SHALL THE COPYRIGHT OWNER OR CONTRIBUTORS BE LIABLE FOR ANY DIRECT, INDIRECT, INCIDENTAL, SPECIAL, EXEMPLARY, OR CONSEQUENTIAL DAMAGES (INCLUDING, BUT NOT LIMITED TO, PROCUREMENT OF SUBSTITUTE GOODS OR SERVICES LOSS OF USE, DATA, OR PROFITS OR BUSINESS INTERRUPTION) HOWEVER CAUSED AND ON ANY THEORY OF LIABILITY, WHETHER IN CONTRACT, STRICT LIABILITY, OR TORT (INCLUDING NEGLIGENCE OR OTHERWISE) ARISING IN ANY WAY OUT OF THE USE OF THIS SOFTWARE, EVEN IF ADVISED OF THE POSSIBILITY OF SUCH DAMAGE.
*/

import React from 'react'
import PropTypes from 'prop-types'

import {
  Table,
  PageNavigationButtons,
  Spinner,
  DeductionIdentityPayments,
  DeductionIdentityPaymentsEdit,
  SearchResultsFeedback,
  Warning
} from "../"

import {
  encodeBase64,
  decodeBase64,
  chunkArray
} from '../../utils'

import { graphql } from 'react-apollo'
import gql from 'graphql-tag'

class DeductionPayments extends React.PureComponent {
  static propTypes = {
    deductionId: PropTypes.string.isRequired,
    payrollCycleId: PropTypes.string.isRequired,
    deductionParameters: PropTypes.string.isRequired,
    filter: PropTypes.string,
    search: PropTypes.string
  }

  state = {
    refetch: false,
    currentPage: 0,
    pages: null,
    identities: [],
    editIdentity: null,
    identityViewed: null
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    if (nextProps.data.error && !this.state.refetch) {
      this.setState({ refetch: true })
      nextProps.data.refetch()
    } else if (this.state.refetch) {
      this.setState({ refetch: false })
    } else if (nextProps.filter !== this.props.filter) {
      this.setState({
        currentPage: 0,
        pages: null,
        identities: [],
        editIdentity: null,
        identityViewed: null
      })
    } else if (nextProps.search.length > 0 && nextProps.search !== this.props.search) {
      this.setState({ currentPage: 0 })
    } else {
      let removedPayments = JSON.parse(this.props.deductionParameters)["removed"]
      let removedPaymentIdentities = Object.keys(removedPayments)
        .map(identity => (
          this.props.search.length > 0
            ? removedPayments[identity].identity_value.includes(this.props.search)
              ? encodeBase64(identity, "IdentityNode")
              : null
            : encodeBase64(identity, "IdentityNode")
        ))
        .filter(identity => identity !== null)

      if (nextProps.data.payments && nextProps.data.payments.edges.length > 0) {
        let payments = nextProps.data.payments.edges.filter(payment => (
          payment.node.transactedIn !== null  // get non-parent payments
        ))

        if (payments.length > 0) {
          // get list of all identities with payments
          let identitiesWithPayments = payments.map(payment => (
            payment.node.identityFor.id
          ))
          // use spread operator with Set to get an array of unique identity ids
          let identities = [...new Set([...identitiesWithPayments, ...removedPaymentIdentities])]
          let identitiesChunked = chunkArray(identities, 5)
          this.setState({
            identities: identitiesChunked,
            pages: identitiesChunked.length - 1
          })
        }
      } else if (removedPaymentIdentities.length > 0) {
        let identitiesChunked = chunkArray(removedPaymentIdentities, 5)
        this.setState({
          identities: identitiesChunked,
          pages: identitiesChunked.length - 1
        })
      }
    }
  }

  getIdentityForName = (payments, identity) => {
    let payerPayment = payments.find(payment => (
      payment.node.identityFor &&  // idenityFor will be 'null' if payment is the parent/batch payment
      payment.node.identityFor.id === identity
    ))

    if (payerPayment) {
      return payerPayment.node.identityFor.fullName
    } else {
      return null
    }
  }

  render() {
    if (this.props.data.loading) {
      return <Spinner type="brand" />
    }

    if (this.props.data.error) {
      return (
        <Warning className='u-font-size-larger slds-m-around_large'>
          We&rsquo;re experiencing a problem loading your data
        </Warning>
      )
    }

    let tableRows = []
    let removedPayments = JSON.parse(this.props.deductionParameters)

    if (this.state.identities.length > 0) {
      tableRows = this.state.identities[this.state.currentPage].map((identityId, index) => {
        return (
          <EmployeePaymentsTableRow
            identityId={identityId}
            deductionId={this.props.deductionId}
            payrollCycleId={this.props.payrollCycleId}
            identityForName={this.props.data.payments
              ? this.getIdentityForName(this.props.data.payments.edges, identityId)
              : null
            }
            deduction={this.props.deductionId}
            readOnly={this.props.readOnly}
            filter={this.props.filter}
            search={this.props.search}
            removedPayments={removedPayments.hasOwnProperty("removed") ? removedPayments.removed : null}
            identityViewed={this.state.identityViewed}
            updateIdentityViewed={identityViewed => this.setState({ identityViewed })}
            editIdentity={this.state.editIdentity}
            setEdit={editIdentity => this.setState({ editIdentity })}
            key={`employee_table_row_${identityId}_${index}`}
          />
        )
      })
    }

    return (
      <div>
        {this.props.search &&
          <SearchResultsFeedback
            resultsFound={tableRows.length > 0}
            numberOfResults={tableRows.length}
            searchString={this.props.search}
            filter={this.props.filter}
            onRemoveFilterClick={this.props.onRemoveSearchFilterClick}
          />
        }
        <Table
          columns={["Name", "Surname", "ID", "Empl#", "Product", "Paid by", "Amount", "Change", ""]}
          rows={tableRows}
          objectRows={true}
          noRowBorders={true}
          className="slds-max-medium-table_stacked-horizontal slds-no-row-hover"
        />
        {this.state.pages
          ? <PageNavigationButtons
              onNextClick={() => this.setState((prevState) => (
                {
                  currentPage: prevState.currentPage + 1,
                  edit: false
                }
              ))}
              onPreviousClick={() => this.setState((prevState) => (
                {
                  currentPage: prevState.currentPage - 1,
                  edit: false
                }
              ))}
              previousDisabled={this.state.currentPage === 0}
              nextDisabled={this.state.currentPage === this.state.pages}
              className="slds-p-around_medium slds-float_right"
            />
          : null
        }
      </div>
    )
  }
}

class EmployeePaymentsTableRow extends React.Component {
  static propTypes = {
    deductionId: PropTypes.string.isRequired,
    payrollCycleId: PropTypes.string.isRequired,
    identityId: PropTypes.string.isRequired,
    identityForName: PropTypes.string,
    removedPayments: PropTypes.object.isRequired,
    filter: PropTypes.string,
    search: PropTypes.string,
    readOnly: PropTypes.bool,
    editIdentity: PropTypes.string,
    identityViewed: PropTypes.string,
    updateIdentityViewed: PropTypes.func,
    setEdit: PropTypes.func
  }

  state = {
    identityViewed: this.props.identityViewed,
    errorOccurredDuringPaymentSave: false
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    this.setState({ identityViewed: nextProps.identityViewed })
  }

  shouldComponentUpdate(nextProps, nextState) {
    if (nextState.errorOccurredDuringPaymentSave) {
      setTimeout(
        () => {
          this.setState({ errorOccurredDuringPaymentSave : false })
        },
        3000
      )
    } else if (this.state.errorOccurredDuringPaymentSave) {
      return false
    }

    return true
  }

  editRow = (identity, identityViewed) => {
    this.props.updateIdentityViewed(identityViewed)
    this.props.setEdit(identity)
  }

  render() {
    if (this.props.editIdentity === this.props.identityId) {
      return (
        <tr className="slds-theme_shade">
          <td colSpan={9}>
            <DeductionIdentityPaymentsEdit
              deductionId={this.props.deductionId}
              payrollCycleId={this.props.payrollCycleId}
              identityId={this.props.identityId}
              identityForName={this.props.identityForName}
              onSave={(identity, errorOccurredDuringPaymentSave) => {
                this.props.updateIdentityViewed(identity)
                if (errorOccurredDuringPaymentSave) {
                  this.setState({ errorOccurredDuringPaymentSave: true })
                }
              }}
              viewMode={identityViewed => this.editRow(null, identityViewed)}
            />
          </td>
        </tr>
      )
    } else {
      return (
        <DeductionIdentityPayments
          identityId={this.props.identityId}
          deductionId={this.props.deductionId}
          removedPayments={this.props.removedPayments}
          filter={this.props.filter}
          search={this.props.search}
          readOnly={this.props.readOnly}
          onEditClick={identity => this.editRow(identity, null)}
          identityViewed={this.state.identityViewed}
          errorOccurredDuringPaymentSave={this.state.errorOccurredDuringPaymentSave}
        />
      )
    }
  }
}

const getPayments = gql`
  query getPayments($id: UUID, $filter: String!, $search: String!, $listRemovedPaymentsOnly: Boolean!) {
    payments(workflowrunDeducting_Id: $id, state: $filter, identityFor_IdentityValue_Icontains: $search, orderBy: "identity_for__last_name") @skip(if: $listRemovedPaymentsOnly) {
      edges {
        node {
          id
          state
          identityFor {
            id
            fullName
          }
          transactedIn {
            id
          }
        }
      }
    }
  }
`

const withData =
  graphql(getPayments, {
    options: ({ deductionId, filter, search }) => ({
      variables: {
        id: decodeBase64(deductionId),
        filter,
        search: search || '',
        listRemovedPaymentsOnly: filter === 'removed'
      },
      fetchPolicy: "network-only"
    })
  })

export default withData(DeductionPayments)



// WEBPACK FOOTER //
// ./src/components/deduction-payments/index.js